import type { CSSProperties } from 'react';

import { isStampLockedByTime, type StampCardStamp } from '../../../../../stores/stamp-card/types';

import { StampCardStampIcon } from './StampCardStampIcon';
import styles from './StampCardStampsRow.module.scss';

interface StampCardStampsRowProps {
  stamps: StampCardStamp[];
  target: number;
  emptyStampAssetUrl?: string | null;
  filledStampAssetUrl?: string | null;
  freeStampAssetUrl?: string | null;
  lockedStampAssetUrl?: string | null;
  isOpening?: boolean;
  isClosing?: boolean;
  justStampedIndexes?: number[];
  justUnlockedIndex?: number | null;
  testIdPrefix?: string;
}

const MAX_STAMPS_PER_ROW = 6;
const DEFAULT_STAMP_SIZE = '24px';
const DEFAULT_STAMP_GAP = '3px';
const COMPACT_STAMP_SIZE = '22px';
const COMPACT_STAMP_GAP = '3px';
const DEFAULT_FILLED_ICON_SIZE = '17px';
const COMPACT_FILLED_ICON_SIZE = '16px';
const DEFAULT_ROW_GAP = '2px';
const COMPACT_ROW_GAP = '1px';

export function StampCardStampsRow(props: StampCardStampsRowProps) {
  const {
    stamps,
    target,
    emptyStampAssetUrl,
    filledStampAssetUrl,
    freeStampAssetUrl,
    lockedStampAssetUrl,
    isOpening,
    isClosing,
    justStampedIndexes,
    justUnlockedIndex,
    testIdPrefix,
  } = props;

  const isCompact = target > MAX_STAMPS_PER_ROW;
  const rowCount = isCompact ? 2 : 1;
  const stampsPerRow = Math.max(Math.ceil(target / rowCount), 1);
  const stampSize = isCompact ? COMPACT_STAMP_SIZE : DEFAULT_STAMP_SIZE;
  const stampGap = isCompact ? COMPACT_STAMP_GAP : DEFAULT_STAMP_GAP;
  const filledIconSize = isCompact ? COMPACT_FILLED_ICON_SIZE : DEFAULT_FILLED_ICON_SIZE;
  const rowGap = isCompact ? COMPACT_ROW_GAP : DEFAULT_ROW_GAP;
  const animated = isOpening !== undefined || isClosing !== undefined;
  const animateState = isClosing ? 'exit' : isOpening ? 'visible' : 'hidden';

  const rows = Array.from({ length: rowCount }, (_, rowIndex) => {
    const start = rowIndex * stampsPerRow;
    const end = Math.min(start + stampsPerRow, target);
    return Array.from({ length: end - start }, (__, i) => start + i + 1);
  });

  return (
    <div
      className={styles.wrap}
      style={
        {
          '--stamp-size': stampSize,
          '--stamp-gap': stampGap,
          '--stamp-filled-icon-size': filledIconSize,
          '--stamp-row-gap': rowGap,
        } as CSSProperties
      }
    >
      <div className={styles.stampsRows}>
        {rows.map((rowStampIndexes, rowIndex) => (
          <div key={rowIndex} className={styles.stampsRow}>
            {rowStampIndexes.map((stampIndex, i) => {
              const isEarned = stamps.some((stamp) => stamp.stampIndex === stampIndex && stamp.status === 'COMPLETED');
              const locked = stamps.some((stamp) => stamp.stampIndex === stampIndex && isStampLockedByTime(stamp));
              const isFree = stamps.some((stamp) => stamp.stampIndex === stampIndex && stamp.isFree);
              const staggerIndex = rowIndex * stampsPerRow + i;
              // Position within this reveal, so several stamps earned together cascade instead of
              // all flashing at once.
              const justStampedOrder = justStampedIndexes?.indexOf(stampIndex) ?? -1;
              const isJustStamped = justStampedOrder >= 0;
              const isJustUnlocked = stampIndex === justUnlockedIndex;
              const status = isEarned ? 'stamped' : locked ? 'locked' : 'empty';

              return (
                <StampCardStampIcon
                  key={stampIndex}
                  animated={animated}
                  animateState={animateState}
                  dataTestId={testIdPrefix ? `${testIdPrefix}-stamp-${stampIndex}-${status}` : undefined}
                  emptyStampAssetUrl={emptyStampAssetUrl}
                  filledStampAssetUrl={filledStampAssetUrl}
                  freeStampAssetUrl={freeStampAssetUrl}
                  isEarned={isEarned}
                  isFree={isFree}
                  isJustStamped={isJustStamped}
                  isJustUnlocked={isJustUnlocked}
                  justStampedOrder={justStampedOrder}
                  locked={locked}
                  lockedStampAssetUrl={lockedStampAssetUrl}
                  staggerIndex={staggerIndex}
                />
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
